import type { HandbookChapter } from '../model';

/**
 * Die Kapitel, die vor allen anderen kommen: Was die Oberflaeche zeigt, wie
 * man hineinkommt, und wer was darf.
 */

const einstieg: HandbookChapter = {
  id: 'einstieg',
  title: 'Erste Schritte',
  summary: 'Wie die Oberfläche aufgebaut ist und wo Sie was finden.',
  audience: 'agent',
  sections: [
    {
      id: 'was-das-system-ist',
      title: 'Was dieses System ist',
      blocks: [
        {
          kind: 'text',
          text: 'ITSCRM ist ein Ticketsystem mit angebautem CRM. Ein Ticket ist ein Vorgang: eine Störung, eine Anfrage, ein Auftrag. Es hat eine Nummer, einen Zustand und einen Verlauf, und es bleibt bestehen, bis es abgeschlossen ist. Alles andere im System hängt daran — die Zeit, die Sie erfassen, die Frist, die läuft, der Kunde, für den Sie arbeiten.',
        },
        {
          kind: 'text',
          text: 'Das CRM ist bewusst schmal gehalten: Kunden, Standorte, Ansprechpartner, Verträge, Kommunikationshistorie. Es ist die Stammdatenseite des Ticketwesens und kein Vertriebswerkzeug. Rechnungen, Anlagenverwaltung, Fernwartung und Projektsteuerung gehören ausdrücklich nicht dazu.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Zwei Oberflächen, ein System',
          text: 'Sie arbeiten in der Bearbeitungsoberfläche unter /app. Ihre Kunden sehen das Self-Service-Portal unter /portal — eine getrennte Anwendung mit eigenem Programmcode, die niemals Bearbeitercode ausliefert. Was ein Kunde dort sieht, entscheidet der Server, nicht die Anzeige. Ein Konto braucht er dafür nicht: E-Mail-Adresse und Vorgangsnummer genügen, um den eigenen Vorgang zu sehen und darin zu antworten.',
        },
      ],
    },
    {
      id: 'aufbau',
      title: 'Der Aufbau der Oberfläche',
      blocks: [
        {
          kind: 'shot',
          file: 'oberflaeche',
          caption: 'Kopfleiste, Navigation links, Arbeitsfläche rechts.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Kopfleiste',
              text: 'Ganz links der Name des Systems, rechts der Mandant, in dem Sie gerade arbeiten, Ihr Name und die Abmeldung. Ein Klick auf Ihren Namen führt zu Ihren eigenen Einstellungen — Kennwort und zweiter Faktor. In der Mitte steht das Feld „Suchen oder springen“ mit dem Tastenkürzel daneben; es öffnet die Befehlspalette.',
            },
            {
              term: 'Navigation links',
              text: 'Ganz oben die Startseite. Darunter „Tickets“ — Ticketliste, Kanban-Board, Anfragen, Termine und Wissensdatenbank —, dann „Kunden“ mit der Kundenliste und der Sicherungsübersicht, dann „Auswertung“ mit Zeitauswertung und Berichten. Darunter die „Einstellungen“, sichtbar nur mit dem Recht dazu, und abgesetzt am Fuß das Handbuch — es gehört zu keinem Bereich, weil es von allen handelt. Mittig darunter steht die Build-Nummer der laufenden Fassung; ein Klick darauf öffnet den Systemstatus. Neben „Anfragen“ steht die Zahl der Mails, die auf eine Sichtung warten, sobald es welche gibt.',
            },
            {
              term: 'Die Einstellungen klappen auf',
              text: 'Zwanzig Einträge untereinander sind kein Menü, sondern ein Verzeichnis. Sie stehen deshalb in Fächern — Tickets, Vorlagen, Kanäle, Benutzer & Rechte, Schnittstellen, Datenschutz —, die zugeklappt beginnen; Sie sehen nur die Fächer, in denen Ihre Rechte wenigstens einen Eintrag erlauben; die Zahl daneben sagt, wie viele Einträge darin liegen. Ein Klick öffnet ein Fach, ein zweiter schließt es, und mehrere dürfen gleichzeitig offen stehen. Was Sie aufgeklappt haben, steht beim nächsten Aufruf wieder offen — es wird im Browser gemerkt und nicht beim Server; an einem anderen Arbeitsplatz beginnen die Fächer daher wieder zu. Rufen Sie eine Seite der Einstellungen auf, öffnet sich ihr Fach von selbst: Sie sollen sehen, wo Sie stehen, ohne suchen zu müssen.',
            },
            {
              term: 'Arbeitsfläche',
              text: 'Der Inhalt der gewählten Ansicht. Ganz oben kann ein Störungsbanner stehen — eine Ankündigung, die alle Bearbeiter betrifft, etwa der Ausfall der Telefonanlage.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Arbeiten ohne Maus',
          text: 'Jede Seite beginnt mit einem Sprunglink „Zum Inhalt springen“. Er wird sichtbar, sobald Sie die Tabulatortaste drücken, und überspringt die Navigation. Ohne ihn müssten Sie sich auf jeder Seite erneut durch die vollständige Navigation bewegen.',
        },
      ],
    },
    {
      id: 'schnell-arbeiten',
      title: 'Schneller als über die Navigation',
      blocks: [
        {
          kind: 'text',
          text: 'Die Befehlspalette ist ein Feld für alles: Sie öffnen sie mit Strg+K (auf dem Mac ⌘K) oder über „Suchen oder springen“ in der Kopfleiste, tippen ein paar Buchstaben und landen im Ticket, beim Kunden oder in einem Bereich der Anwendung. Sie ersetzt die Navigation nicht — sie erspart den Weg dorthin. Für die Verwaltung ist sie der kürzeste: Die Palette kennt jeden der fünfzehn Punkte, ohne dass Sie sein Fach aufklappen müssen.',
        },
        {
          kind: 'shot',
          file: 'befehlspalette',
          caption: 'Die Befehlspalette: Treffer nach Bereich gruppiert, Bedienung unten.',
        },
        {
          kind: 'steps',
          steps: [
            { text: 'Strg+K drücken (auf dem Mac ⌘K).' },
            {
              text: 'Tippen, wonach Sie suchen.',
              detail:
                'Ohne Eingabe stehen die Bereiche der Anwendung da. Ab zwei Zeichen kommen Tickets und Kunden vom Server dazu — Tickets zuerst, weil das die häufigste Suche im Tagesgeschäft ist.',
            },
            { text: 'Mit den Pfeiltasten wählen, mit der Eingabetaste öffnen.' },
            { text: 'Esc schließt die Palette, ohne etwas zu tun.' },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Sie zeigt nur, was Sie ohnehin sehen dürfen',
          text: 'Die Bereiche sind nach denselben Rechten gefiltert wie die Navigation, und Tickets und Kunden kommen aus denselben Abfragen wie die Listen — mit derselben Mandantentrennung. Die Palette ist eine Abkürzung und kein zweiter Weg an den Daten vorbei.',
        },
        {
          kind: 'table',
          head: ['Taste', 'Wirkung', 'Wo'],
          rows: [
            ['Strg+K / ⌘K', 'Befehlspalette öffnen und schließen', 'überall'],
            ['?', 'Übersicht der Tastenkürzel', 'überall'],
            ['A', 'Antwort an den Kunden schreiben', 'im Ticket'],
            ['N', 'Interne Notiz schreiben', 'im Ticket'],
            ['Z', 'Zum Reiter „Zeit“ springen', 'im Ticket'],
            ['Strg+↵ / ⌘↵', 'Beitrag absenden', 'im Editor'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Einzelne Buchstaben stören das Tippen nicht',
          text: 'A, N und Z wirken nur außerhalb von Eingabefeldern. Wer den Buchstaben in einen Betreff schreibt, schreibt ihn dorthin — und löst nichts aus.',
        },
      ],
    },
    {
      id: 'auswahlfelder',
      title: 'Auswahlfelder: tippen statt blättern',
      blocks: [
        {
          kind: 'text',
          text: 'Jede Auswahl in der Anwendung ist ein Feld, das sich wie eine Aufklappliste bedient — und in das Sie tippen können: Kunde, Ticket, Bearbeiter, Kategorie, Warteschlange, Priorität und alle übrigen. Ein Klick öffnet die Liste, ein Klick auf einen Eintrag wählt ihn. Wer tippt, grenzt die Liste ein: „mus“ lässt nur noch „Muster GmbH“ stehen. Die Buchstaben dürfen irgendwo im Namen stehen, Groß- und Kleinschreibung spielt keine Rolle.',
        },
        {
          kind: 'shot',
          file: 'auswahlfeld',
          caption: 'Das Kundenfeld beim Anlegen eines Tickets: drei Buchstaben, ein Treffer.',
        },
        {
          kind: 'table',
          head: ['Feld', 'Was es findet'],
          rows: [
            ['Kunde', 'Name oder Kundennummer, auch teilweise'],
            [
              'Ticket (Zusammenführen, Verknüpfen, Übergeordnet, Regeltest)',
              'Ticketnummer oder Betreff, auch teilweise — „57“ findet T-2026-000057. Ohne Eingabe stehen die zuletzt geänderten Tickets oben.',
            ],
            [
              'Alle übrigen Auswahlen (Bearbeiter, Kategorie, Warteschlange, Priorität, Vorlage …)',
              'Den Namen des Eintrags, auch teilweise. Eine leere Wahl wie „Ohne Kategorie“ steht mit in der Liste.',
            ],
            [
              'Mehrfachauswahl (Tags, Genehmiger, Bearbeiter im Audit)',
              'Wie oben; das Gewählte steht als Marke über dem Feld, ein Klick auf die Marke nimmt es wieder heraus.',
            ],
            [
              'Freitext mit Vorschlägen (Branche, Funktion, Ort, Land, eigene Textfelder)',
              'Werte, die schon erfasst sind — die häufigsten zuerst. Was Sie tippen, gilt auch ohne Wahl; ein Vorschlag erspart nur eine dritte Schreibweise desselben Begriffs.',
            ],
            [
              'E-Mail-Adresse (Datenschutz, Berichtsversand)',
              'Ansprechpartner aller Kunden nach Name oder Adresse; beim Berichtsversand zusätzlich die Bearbeiter',
            ],
          ],
        },
        {
          kind: 'steps',
          steps: [
            { text: 'Ins Feld klicken — die Liste öffnet sich.' },
            { text: 'Tippen, bis der gesuchte Eintrag oben steht.' },
            {
              text: 'Mit den Pfeiltasten wählen und mit der Eingabetaste übernehmen — oder klicken.',
            },
            {
              text: 'Esc schließt die Liste und lässt die bisherige Auswahl stehen.',
              detail:
                'Steht das Feld in einem Dialog, bleibt der Dialog offen. Erst ein zweites Esc schließt ihn.',
            },
            { text: 'Das × rechts im Feld leert die Auswahl — wo eine leere Wahl erlaubt ist.' },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Die Liste endet nicht nach der ersten Seite',
          text: 'Kunden, Tickets und Ansprechpartner sucht das Feld beim Server; alle anderen Listen sind ohnehin vollständig geladen und werden beim Tippen nur gefiltert. Die Liste zeigt die zwanzig besten Treffer zu dem, was Sie getippt haben — der fünfhundertste Kunde ist damit genauso erreichbar wie der erste. Bei E-Mail-Adressen dürfen Sie auch eine Adresse eintippen, die nicht im CRM steht; die Eingabetaste übernimmt sie.',
        },
      ],
    },
    {
      id: 'wo-anfangen',
      title: 'Womit Sie morgens anfangen',
      blocks: [
        {
          kind: 'steps',
          steps: [
            {
              text: 'Startseite öffnen.',
              detail: 'Sie zeigt, was offen ist, was die Frist reißt und was gestern hereinkam.',
            },
            {
              text: 'Den Bereich „Meine Arbeit“ auf der Startseite ansehen.',
              detail:
                'Dort stehen die fünf offenen Tickets auf Ihrem Namen mit der knappsten Frist — sortiert nach der Frist und nicht nach dem Datum, weil das älteste Ticket nicht das dringendste ist.',
            },
            {
              text: 'Darunter „Heutige Termine“ und „Termine für morgen“ ansehen.',
              detail:
                'Dort stehen die Termine aus den Tickets, bei denen Sie eingetragen sind — siehe das Kapitel „Termine“.',
            },
            {
              text: 'Von dort in die vollständige Liste, oder in der Ticketliste auf „Meine Tickets“ schalten.',
              detail:
                'Gespeicherte Ansichten legen Sie sich selbst an — siehe das Kapitel zur Ticketliste.',
            },
            {
              text: 'Überfällige zuerst.',
              detail:
                'Die SLA-Uhr färbt sich, bevor die Frist reißt. Ein rotes Ticket ist bereits zu spät; ein gelbes ist die Gelegenheit, es nicht werden zu lassen.',
            },
          ],
        },
        { kind: 'route', to: '/app/dashboard', label: 'Zur Startseite' },
      ],
    },
  ],
};

const anmeldung: HandbookChapter = {
  id: 'anmeldung',
  title: 'Anmeldung und eigener Zugang',
  summary: 'Anmelden, Mandant wechseln, zweiten Faktor einrichten, Kennwort ändern.',
  audience: 'agent',
  route: '/app/security',
  sections: [
    {
      id: 'anmelden',
      title: 'Anmelden',
      blocks: [
        {
          kind: 'shot',
          file: 'anmeldung',
          caption: 'Die Anmeldemaske. Der zweite Faktor wird erst nach dem Kennwort abgefragt.',
        },
        {
          kind: 'steps',
          steps: [
            { text: 'E-Mail-Adresse und Kennwort eingeben, „Anmelden“ klicken.' },
            {
              text: 'Wenn Ihr Zugang einen zweiten Faktor verlangt: den sechsstelligen Code aus Ihrer Authenticator-App eingeben.',
              detail:
                'Das Codefeld erscheint erst jetzt. Das ist Absicht — vor der richtigen Kennworteingabe verrät das System nicht, ob es diesen Zugang überhaupt gibt.',
            },
            {
              text: 'Wenn Sie in mehreren Mandanten arbeiten: den Mandanten wählen.',
              detail:
                'Sie arbeiten immer in genau einem Mandanten. Ein Wechsel ist jederzeit über die Kopfleiste möglich.',
            },
          ],
        },
        {
          kind: 'shot',
          file: 'anmeldung-zweiter-schritt',
          caption:
            'Der zweite Schritt: Kennung und Kennwort bleiben stehen, gefragt ist nur noch der Code.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: '„Noch ein Schritt" ist keine Fehlermeldung',
          text: 'Der blaue Kasten sagt, dass Ihr Kennwort angenommen wurde und nur noch der Code fehlt. Kennung und Kennwort müssen Sie nicht erneut eingeben — sie stehen bereits im Formular. Gelb wird der Kasten erst, wenn der Code nicht stimmt oder schon verbraucht war; rot nur bei einer echten Störung.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Der Mandant bestimmt alles, was Sie sehen',
          text: 'Tickets, Kunden, Stammdaten und Auswertungen gehören zu genau einem Mandanten. Was Sie in „Muster GmbH“ nicht finden, kann in „Beispiel AG“ liegen. Die Trennung setzt die Datenbank durch, nicht die Anzeige — es gibt keine Ansicht, die beides gleichzeitig zeigt.',
        },
      ],
    },
    {
      id: 'zweiter-faktor',
      title: 'Zweiten Faktor einrichten',
      blocks: [
        {
          kind: 'text',
          text: 'Wer Rechte trägt, mit denen sich das System umbauen oder personenbezogene Daten im Bestand erreichen lassen, braucht einen zweiten Faktor. Ohne ihn beantwortet der Server jeden fachlichen Aufruf mit einer Ablehnung — die Oberfläche zeigt dann direkt die Einrichtung statt der Navigation.',
        },
        {
          kind: 'table',
          head: ['Recht', 'Warum es einen zweiten Faktor verlangt'],
          rows: [
            ['masterdata.write', 'Baut den Mandanten um: Warteschlangen, Zustände, Prioritäten'],
            ['automation.write', 'Ändert Tickets im Bestand, ohne dass jemand zusieht'],
            ['customfields.write', 'Bestimmt, welche Felder es gibt — und damit, was erhoben wird'],
            ['mail.manage', 'Kommt an Postfachkennwörter'],
            ['ticket.delete', 'Löscht unumkehrbar'],
            ['crm.anonymize', 'Entfernt den Personenbezug unumkehrbar'],
            ['time.manage', 'Ändert fremde Zeiten'],
            ['privacy.manage', 'Auskunftsexporte, Löschläufe, Protokollansicht'],
            ['apitoken.manage', 'Stellt langlebige Vollmachten aus'],
          ],
        },
        {
          kind: 'shot',
          file: 'zweiter-faktor',
          caption:
            'Die Einrichtung zeigt den QR-Code und verlangt einen ersten gültigen Code als Bestätigung.',
        },
        {
          kind: 'steps',
          steps: [
            { text: 'In der Kopfleiste auf Ihren Namen klicken.' },
            {
              text: 'Das Kennwort erneut eingeben.',
              detail:
                'Die Maske verlangt es ein zweites Mal, damit niemand an einem unbeaufsichtigten Rechner den zweiten Faktor auf sein eigenes Telefon umlenken kann.',
            },
            { text: 'Den QR-Code mit einer Authenticator-App abfotografieren.' },
            {
              text: 'Den angezeigten Code eintragen und bestätigen.',
              detail:
                'Erst damit ist der Faktor scharf. Ein Fehler bei der Übernahme fällt so sofort auf und nicht bei der nächsten Anmeldung.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'danger',
          title: 'Es gibt keine Wiederherstellungscodes',
          text: 'Wer sein Telefon verliert, kommt an keinen fachlichen Endpunkt mehr heran. Der einzige Rückweg führt über einen Administrator, der den zweiten Faktor unter „Benutzer“ zurücksetzt. Das ist Absicht: Ein ausgedruckter Wiederherstellungscode im Schreibtisch ist der zweite Faktor, den man mit einem Griff umgeht.',
        },
      ],
    },
    {
      id: 'benachrichtigungen',
      title: 'Benachrichtigungen',
      blocks: [
        {
          kind: 'text',
          text: 'Unter Ihrem Namen in der Kopfleiste stellen Sie ein, wovon Sie per E-Mail erfahren möchten. Was nicht abgewählt ist, kommt — und wer selbst gehandelt hat, bekommt nichts. Das ist der Unterschied zwischen einem nützlichen Postfach und einem, das man wegfiltert.',
        },
        {
          kind: 'terms',
          items: [
            { term: 'Mir wird ein Ticket zugewiesen', text: 'Der wichtigste Anlass.' },
            {
              term: 'Ein Ticket, das ich beobachte, wechselt den Status',
              text: 'Für Vorgänge, die Sie verfolgen, ohne sie zu führen.',
            },
            {
              term: 'Zu einem meiner Tickets gibt es einen neuen Beitrag',
              text: 'Der Kunde hat geantwortet.',
            },
            { term: 'Jemand erwähnt mich in einem Beitrag', text: 'Ein Kollege braucht Sie.' },
            {
              term: 'Ich soll eine Genehmigung erteilen',
              text: 'Jemand wartet auf Ihre Entscheidung.',
            },
            {
              term: 'Eine Frist an einem meiner Tickets läuft ab',
              text: 'Die Warnschwelle der SLA-Uhr ist erreicht.',
            },
          ],
        },
      ],
    },
    {
      id: 'abwesenheit',
      title: 'Abwesenheit und Vertretung',
      blocks: [
        {
          kind: 'text',
          text: 'Tragen Sie ein, bis wann Sie weg sind und wer übernimmt. Sie bleiben zuweisbar — der Server leitet die Zuweisung auf die Vertretung um und vermerkt das am Ticket. Niemand muss wissen, dass Sie im Urlaub sind, damit die Arbeit ankommt.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Vor dem Urlaub',
          text: 'Vertretung eintragen, offene Tickets durchsehen, was länger dauert auf Wiedervorlage legen. In der Ticketliste steht an einem abwesenden Bearbeiter „abwesend“ — Ihre Kollegen sehen es also auch.',
        },
      ],
    },
    {
      id: 'kennwort',
      title: 'Kennwort ändern',
      blocks: [
        {
          kind: 'steps',
          steps: [
            { text: 'In der Kopfleiste auf Ihren Namen klicken.' },
            { text: 'Bisheriges Kennwort, neues Kennwort, Wiederholung eingeben.' },
            {
              text: 'Speichern.',
              detail:
                'Alle Ihre übrigen Anmeldungen enden damit. Wer sein Kennwort ändert, weil es abhandengekommen sein könnte, will genau das.',
            },
          ],
        },
      ],
    },
  ],
};

const rollen: HandbookChapter = {
  id: 'rollen-und-rechte',
  title: 'Rollen und Rechte',
  summary: 'Welche Rolle was darf — und warum ein Knopf fehlt, den ein Kollege sieht.',
  audience: 'agent',
  sections: [
    {
      id: 'grundsatz',
      title: 'Der Grundsatz',
      blocks: [
        {
          kind: 'text',
          text: 'Jeder Zugang trägt eine Rolle, jede Rolle ein Bündel von Rechten. Fehlt Ihnen ein Recht, blendet die Oberfläche den zugehörigen Knopf aus. Das ist Bequemlichkeit und keine Absicherung: Der Server prüft jeden einzelnen Aufruf ohnehin. Wer eine Adresse von Hand eingibt, für die ihm das Recht fehlt, bekommt eine Ablehnung — und bei fremden Objekten die Auskunft „gibt es nicht“ statt „dürfen Sie nicht“, damit die bloße Existenz nicht bestätigt wird.',
        },
      ],
    },
    {
      id: 'rollen',
      title: 'Die mitgelieferten Rollen',
      blocks: [
        {
          kind: 'terms',
          items: [
            {
              term: 'Administration',
              text: 'Vollzugriff auf den Mandanten einschließlich Einstellungen, Datenschutzwerkzeugen und API-Token. Verlangt einen zweiten Faktor.',
            },
            {
              term: 'Bearbeitung',
              text: 'Tickets anlegen, bearbeiten, im Zustand wechseln, Beiträge schreiben. Dazu Kundendaten pflegen, Wissensartikel schreiben, eigene Zeiten erfassen, Berichte lesen und Ankündigungen schreiben. Sonst kein Zugriff auf die Einstellungen.',
            },
            {
              term: 'Bearbeitung, nur lesend',
              text: 'Einsicht in Tickets, Stammdaten, Kunden und Wissensartikel — ohne jede Änderungsmöglichkeit. Für Bereitschaft, Einarbeitung, Revision.',
            },
            {
              term: 'Kundenverantwortung',
              text: 'Ein Portalzugang, kein Bearbeiterzugang. Sieht im Portal alle Tickets des eigenen Kunden, nicht nur die selbst gemeldeten.',
            },
          ],
        },
        {
          kind: 'text',
          text: 'Was jede Rolle im Einzelnen darf, steht unter Einstellungen → Benutzer & Rechte → „Rollen“. Passt keine, legt die Administration dort eine eigene an — am einfachsten als Kopie einer mitgelieferten.',
        },
      ],
    },
    {
      id: 'was-bearbeiter-nicht-duerfen',
      title: 'Was die Rolle „Bearbeitung“ nicht darf',
      blocks: [
        {
          kind: 'table',
          head: ['Nicht möglich', 'Wer es kann'],
          rows: [
            ['Warteschlangen, Status, Prioritäten ändern', 'Administration'],
            ['Servicezusagen und Geschäftszeiten festlegen', 'Administration'],
            ['Eigene Felder anlegen', 'Administration'],
            ['Regeln, Zeitpläne und Webhooks einrichten', 'Administration'],
            ['Postfächer und Mailfilter verwalten', 'Administration'],
            ['Mitarbeiter anlegen, Kennwörter zurücksetzen', 'Administration'],
            ['Wissensartikel freigeben und veröffentlichen', 'Administration'],
            ['Fremde Zeiten nachtragen oder ändern', 'Administration'],
            ['Tickets löschen', 'Administration'],
            ['Ansprechpartner anonymisieren', 'Administration'],
            ['Auskunftsexporte und Aufbewahrungsläufe', 'Datenschutzrecht (privacy.manage)'],
            ['API-Token ausstellen und widerrufen', 'Administration'],
            ['Eigene Rollen anlegen und ändern', 'Administration'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Wenn Ihnen ein Knopf fehlt',
          text: 'Erst prüfen, ob Sie im richtigen Mandanten sind. Dann prüfen, ob die Rolle stimmt — Ihr Administrator sieht unter „Benutzer“, welche Sie tragen, und unter „Rollen“, was sie gewährt. Ein fehlender Knopf ist nie ein Anzeigefehler.',
        },
      ],
    },
  ],
};

export const grundlagenChapters: HandbookChapter[] = [einstieg, anmeldung, rollen];
